/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Yakov Gushchin
 * @version 1.16.0
 * @since 1.14.0
 */

import { useCallback } from 'react';

import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { InfoBlock } from '../../../components/common';
import DatePicker from '../../../components/common/DatePicker/DatePicker';
import { EColorStatus } from '../../../modules/app/appConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardChangeLock,
} from '../../../modules/internationalization/intSelectors';
import ICommonTranslation from '../../../modules/internationalization/types/common';
import { IWizardChangeLockTranslation } from '../../../modules/internationalization/types/wizards/changeLock';
import { IRootReducer } from '../../../reducers/rootReducer';
import { stringFormatParams } from '../../../utils/stringFormat';
import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import { TWizardChangeLockSetParamsPayload } from '../types/wizardChangeLockActionTypes';
import { wizardChangeLockSetParamsAction } from '../wizardChangeLockActions';
import { EWizardChangeLockSteps } from '../wizardChangeLockConstant';
import { IWizardChangeLockReducer } from '../wizardChangeLockReducer';
import {
    makeSelectWizardChangeLockDateFrom,
    makeSelectWizardChangeLockDateTo,
    makeSelectWizardChangeLockIsUnlockSubscribers,
    makeSelectWizardChangeLockLockParameters,
    makeSelectWizardChangeLockMaxLockingPeriod,
} from '../wizardChangeLockSelectors';
import { StepSelectDateFilterStyled, WizardChangeLockStyled } from './styled/WizardChangeLockStyled';

const WizardChangeLockDateSelection = (props: IConnectedProps & IDispatchProps) => {
    const {
        dateFrom,
        dateTo,
        isUnlockSubscribers,
        lockParameters,
        maxLockingPeriod,
        translation,
        translationCommon,

        saveCurrentStepState,
    } = props;

    const stepTranslation = translation[EWizardChangeLockSteps.StepDateSelection];

    const setDateToHandler = useCallback((date: Moment) => {
        saveCurrentStepState({ dateTo: date.format(EFormatData.FullData) });
    }, [saveCurrentStepState]);

    const setDateFromHandler = useCallback((date: Moment) => {
        const currentDateTo = moment(dateTo, EFormatData.FullData);
        saveCurrentStepState({
            dateFrom: date.format(EFormatData.FullData),
            dateTo: (date.isAfter(currentDateTo) ? date : currentDateTo).format(EFormatData.FullData),
        });
    }, [dateTo, saveCurrentStepState]);

    return <WizardChangeLockStyled>
        <div dangerouslySetInnerHTML={{
            __html: stringFormatParams(
                isUnlockSubscribers ? stepTranslation.maxUnlockingPeriodInfo : stepTranslation.maxLockingPeriodInfo,
                { maxLockingPeriod: maxLockingPeriod || 0 },
            ),
        }}/>
        <div className="wizard-change-lock__title">
            {isUnlockSubscribers ? stepTranslation.unlockingPeriodLabel : stepTranslation.lockingPeriodLabel}
        </div>
        <div>
            <StepSelectDateFilterStyled>
                <div className="wizard-change-lock__title">
                    {isUnlockSubscribers
                        ? stepTranslation.unlockDateFromLabel
                        : stepTranslation.lockDateFromLabel}
                </div>
                <DatePicker
                    maxDate={moment(lockParameters?.dateFromEnd, EFormatData.FullData)}
                    minDate={moment(lockParameters?.dateFrom, EFormatData.FullData)}
                    selectedDate={moment(dateFrom ? dateFrom : lockParameters?.dateFrom, EFormatData.FullData)}
                    translation={translationCommon}
                    onSelectDate={setDateFromHandler}
                />
            </StepSelectDateFilterStyled>
            {
                !isUnlockSubscribers && <StepSelectDateFilterStyled>
                    <div className="wizard-change-lock__title">
                        {stepTranslation.lockDateToLabel}
                    </div>
                    <DatePicker
                        maxDate={moment(lockParameters?.dateToEnd, EFormatData.FullData)}
                        minDate={moment(dateFrom ? dateFrom : lockParameters?.dateToStart, EFormatData.FullData)}
                        selectedDate={moment(dateTo ? dateTo : lockParameters?.dateTo, EFormatData.FullData)}
                        translation={translationCommon}
                        onSelectDate={setDateToHandler}
                    />
                </StepSelectDateFilterStyled>
            }
        </div>

        <div className="wizard-change-lock__info-block">
            <InfoBlock colorStatus={EColorStatus.WARNING}>
                <div dangerouslySetInnerHTML={{
                    __html: stepTranslation.lockingPeriodInfo,
                }}/>
            </InfoBlock>
        </div>
    </WizardChangeLockStyled>;
};

export interface IConnectedProps extends Pick<IWizardChangeLockReducer,
    'dateFrom' | 'dateTo' | 'isUnlockSubscribers' | 'lockParameters' | 'maxLockingPeriod'> {
    translation: IWizardChangeLockTranslation;
    translationCommon: ICommonTranslation;
}

interface IDispatchProps {
    saveCurrentStepState: (payload: TWizardChangeLockSetParamsPayload) => void;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    dateFrom: makeSelectWizardChangeLockDateFrom(),
    dateTo: makeSelectWizardChangeLockDateTo(),
    isUnlockSubscribers: makeSelectWizardChangeLockIsUnlockSubscribers(),
    lockParameters: makeSelectWizardChangeLockLockParameters(),
    maxLockingPeriod: makeSelectWizardChangeLockMaxLockingPeriod(),
    translation: makeSelectTranslationWizardChangeLock(),
    translationCommon: makeSelectTranslationCommon(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    saveCurrentStepState: (payload: TWizardChangeLockSetParamsPayload) =>
        dispatch(wizardChangeLockSetParamsAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardChangeLockDateSelection);
